import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { FeaturedStockResponse } from '../../../server/response/responseScheme';
import deviceStore from '../../stores/deviceStore';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';
import Carousel, { CarouselProvider } from '../common/Carousel';
import { useCommonNavigate } from '../common/CommonLink';
import { MarkdownViewer } from '../common/editor/MarkdownViewer';

class ViewStore {
    featuredStockList: FeaturedStockResponse[] = [];
    isHovered = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        const [featuredStockList] = await Promise.all([
            apiClient.featuredStock.public.getFeaturedStockList.$query(),
        ]);

        runInAction(() => {
            this.featuredStockList = _.castArray(featuredStockList);
        });
    }

    setHovered(hovered: boolean) {
        this.isHovered = hovered;
    }
}

const FeaturedStockCard = observer(() => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    const featuredStockList = viewStore.featuredStockList;

    return (
        <CarouselProvider autoHeight={true}>
            <div
                className="relative select-none"
                onMouseEnter={(e) => {
                    viewStore.setHovered(true);
                }}
                onMouseLeave={(e) => {
                    viewStore.setHovered(false);
                }}
            >
                <Carousel>
                    <Carousel.Container>
                        {featuredStockList.map((featuredStock) => (
                            <Carousel.Slide key={featuredStock.ticker}>
                                <FeaturedStockCardItem featuredStock={featuredStock} />
                            </Carousel.Slide>
                        ))}
                    </Carousel.Container>
                </Carousel>

                {/* 이전 버튼 (데스크탑 전용) */}
                {deviceStore.hasHoverCapability && (
                    <Carousel.PrevCircleButton
                        isShow={viewStore.isHovered}
                        className="absolute top-1/2 -left-4.5 -translate-y-1/2"
                    />
                )}

                {/* 다음 버튼 (데스크탑 전용) */}
                {deviceStore.hasHoverCapability && (
                    <Carousel.NextCircleButton
                        isShow={viewStore.isHovered}
                        className="absolute top-1/2 -right-4.5 -translate-y-1/2"
                    />
                )}
            </div>
            <Carousel.Dots className="mt-4" />
        </CarouselProvider>
    );
});

const FeaturedStockCardItem = observer(
    ({ featuredStock }: { featuredStock: FeaturedStockResponse }) => {
        const { navigate } = useCommonNavigate();

        return (
            <div
                role="button"
                className="bg-base-200 border-base-300 mx-5 min-h-30 rounded-lg p-4 text-sm"
                onClick={(e) => {
                    e.preventDefault();
                    navigate(`/featured?ticker=${featuredStock.ticker}`);
                }}
            >
                <div className="flex items-center gap-2">
                    <div className="text-base font-semibold">{featuredStock.stock.nameKo}</div>
                    {featuredStock.newsImpact === 'positive' && (
                        <div className="badge badge-xs badge-outline badge-success shrink-0">
                            긍정
                        </div>
                    )}
                    {featuredStock.newsImpact === 'neutral' && (
                        <div className="badge badge-xs badge-outline shrink-0">중립</div>
                    )}
                    {featuredStock.newsImpact === 'negative' && (
                        <div className="badge badge-xs badge-outline badge-error shrink-0">
                            부정
                        </div>
                    )}
                </div>
                <MarkdownViewer
                    className="mt-2"
                    markdown={[featuredStock.reasonSummary].join('\n')}
                />
            </div>
        );
    },
);

export default FeaturedStockCard;
